import { JSX } from 'react';
import ReactDomServer from 'react-dom/server';
import { isObject, isString } from 'underscore';
import TextAreaAutosize from 'react-textarea-autosize';

import { EmailContent, EmailDesign, regCodePlaceholder } from '../scheduled-emails/event-email';
import { InitialAspectRatioOptionsEnum } from '@/types/images.types';
import Quill from '@ui/quill/quill';
import ImageUpload from '@ui/image-upload/image-upload';
import { IScheduledEmailContent } from '@/types/email.types';
import { ScheduledEmailProps } from '../scheduled-emails/create-email';

type emailContentTypes = 'session' | 'event';

interface Props {
  onEdit: ScheduledEmailProps['onEdit'];
  design: EmailDesign;
  emailContent: EmailContent;
  emailContentType: emailContentTypes;
  // editableContent: Record<string, JSX.Element>;
  // Everything in events-3 is painfully typed as any, so this is painful to get right - need to rush through so settingto "any"
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editableContent: Record<string, any>;
  customContent?: IScheduledEmailContent;
  buttonText?: string;
  sent?: boolean;
}

interface ReturnProps<T> {
  getCellContent: (key: keyof T) => JSX.Element;
  getButton: () => JSX.Element;
  getImage: () => JSX.Element;
  // EditImageModal: JSX.Element;
}

function useEditableEmailFields<T>(props: Props): ReturnProps<T> {
  const { onEdit, design, emailContentType, emailContent, editableContent, customContent, buttonText, sent } = props;

  // const [imageModalOpen, setImageModalOpen] = useState<ImageUploadOptions>(null);

  const getCellContent = (key: keyof T) => {
    if (onEdit && !sent) {
      let stringValue = '';
      // const stringValue = customContent?.[key] ?? ReactDomServer.renderToString(editableContent[key]);
      const isCustomKey = customContent && key in customContent;
      if (customContent && isObject(customContent) && isCustomKey) {
        const customValue = customContent[key as unknown as keyof IScheduledEmailContent];
        if (typeof customValue === 'string') {
          stringValue = customValue;
        }
      }
      if (!stringValue && editableContent && isObject(editableContent) && key in editableContent) {
        const editableValue = editableContent[key as string];
        if (typeof editableValue === 'string') {
          stringValue = ReactDomServer.renderToString(editableValue);
        }
      }

      if (!isString(stringValue)) return <></>;

      return (
        <Quill
          html={stringValue}
          onBlurChanged={(v) => (v === undefined ? undefined : onEdit?.(v, key as string))}
          disableAnchorTargets
        />
      );
    }

    const jsxValue = customContent?.[key as unknown as keyof IScheduledEmailContent] ? (
      <div dangerouslySetInnerHTML={{ __html: customContent[key] as string }} />
    ) : (
      editableContent[key as string]
    );
    return jsxValue;
  };

  const getButton = (): JSX.Element => {
    const buttonValue = customContent?.button ?? buttonText ?? '';

    if (onEdit && isString(buttonValue) && !sent) {
      return (
        <div style={design.styles.button} className="email-button-editor-wrapper">
          {/*
            Note: This TextAreaAutosize component is EditableTextSlim in events-3.
            Only adding this comment because this email component has not been UI tested in brandlive-platform.
            We will eventually be bringing in UI editing into this app, so this might not look 100% correct when we do
            and it might require some styling adjustments at that time - however, would very much like to avoid bringing in EditableTextSlim
            from events-3 into this app.
          */}
          <TextAreaAutosize
            onBlur={(e) => onEdit?.(e.target.value.trim(), 'button')}
            className="clear-textarea S3"
            minRows={1}
            maxRows={1}
            defaultValue={buttonValue}
            maxLength={65}
          />
        </div>
      );
    }

    // all emails get the placeholder so if the recipient is a registered attendee they will be taken straight into the event
    // the email sender removes the placeholder for unregistered attendees
    const url = `${emailContent[emailContentType]?.url}${regCodePlaceholder}`;

    return (
      <a
        href={url}
        data-saferedirecturl={emailContent[emailContentType]?.url}
        target="_blank"
        rel="noreferrer"
        style={design.styles.button}
        tabIndex={sent ? -1 : undefined}
      >
        {buttonValue}
      </a>
    );
  };

  const getImage = (): JSX.Element => {
    if (isString(customContent?.primary_image)) return <></>;
    let imageUrl = '';
    if (isObject(customContent?.primary_image) && !Array.isArray(customContent?.primary_image)) {
      imageUrl = customContent?.primary_image?.edited ?? customContent?.primary_image?.original ?? '';
    } else if (emailContent[emailContentType]?.image) {
      imageUrl = emailContent[emailContentType]?.image;
    }

    const isSessionEmail = emailContentType === 'session';
    const imageAspectRatio = isSessionEmail ? '56.25%' : '30%';
    const imageMargins = isSessionEmail ? '20px 0' : '0 0 20px';

    // height placeholder if there is no image
    if (!imageUrl) {
      return (
        <td align="center">
          <span>&nbsp;</span>
        </td>
      );
    }

    if (onEdit) {
      return (
        <td
          align="center"
          className="primary-image-editor-wrapper"
          style={{
            ...design.styles.primaryImage,
            // paddingTop is what is handling the aspect ratio. (16:9 = 9/16 = .5625)
            paddingTop: imageAspectRatio,
            margin: imageMargins,
            backgroundImage: `url(${imageUrl})`,
          }}
        >
          <ImageUpload
            img={
              isObject(customContent?.primary_image) && !Array.isArray(customContent?.primary_image)
                ? customContent?.primary_image?.original
                : ''
            }
            disableEditingAspectRatio
            initialAspectRatio={InitialAspectRatioOptionsEnum[emailContentType === 'session' ? '16:9' : '10:3']}
            onUpload={(image) => {
              onEdit?.(image, 'primary_image');
              return Promise.resolve();
            }}
          />
        </td>
      );
    }

    return (
      <td
        align="center"
        style={{
          ...design.styles.primaryImage,
          backgroundImage: `url(${imageUrl})`,
          paddingTop: imageAspectRatio,
          margin: imageMargins,
        }}
      />
    );
  };

  return {
    getCellContent,
    getButton,
    getImage,
  };
}

export default useEditableEmailFields;
